import { useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Button } from "../../ui/button";
import { ChevronLeft, Settings, Database, Shield, Globe, FolderOpen } from "lucide-react";
import ApplicationControlTab from "../Resources/tabs/ApplicationControlTab";
import DataAccessTab from "../Resources/tabs/DataAccessTab";
import PrivilegeManagementTab from "../Resources/tabs/PrivilegeManagementTab";
import URLControlTab from "../Resources/tabs/URLControlTab";
import { POLICY_PROFILES_PATH } from "./helper";

const tabs = [
    { id: "app-control", name: "Application control", icon: Settings },
    { id: "data-access", name: "Data access", icon: Database },
    { id: "privilege-mgmt", name: "Privilege management", icon: Shield },
    { id: "url-control", name: "URL control", icon: Globe }
];


export default function PolicyProfilesResources() {
    const location = useLocation();
    const { id, os: osParam } = useParams();
    const os = osParam || 'windows';
    const [activeTab, setActiveTab] = useState("app-control");
    let profileName = location.state?.profileName;
    if (!profileName) {
        profileName = localStorage.getItem('currentProfileName') || "Unknown Profile";
    }
    const profileId = id ? Number(id) : -1;
    const navigate = useNavigate();


    const getOSType = (name: string) => {
        const lowerName = name.toLowerCase();
        if (lowerName.includes('windows')) return 'windows';
        if (lowerName.includes('linux')) return 'linux';
        if (lowerName.includes('mac')) return 'mac';
        return 'windows'; // default
    };

    const osType = getOSType(profileName);

    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
    };

    const handleBackToProfiles = () => {
        navigate(POLICY_PROFILES_PATH);
    };

    return (
        <div className="flex-1 overflow-auto bg-background">
            <div className="px-6 py-4 border-b bg-background">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={handleBackToProfiles}
                        className="p-0 h-auto hover:bg-transparent"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <button
                        type="button"
                        onClick={handleBackToProfiles}
                        className="hover:text-foreground"
                    >
                        Policy Profiles
                    </button>
                    <span>/</span>
                    <span className="text-foreground font-medium">{decodeURIComponent(profileName)}</span>
                </div>
            </div>

            {/* Profile Header */}
            <div className="px-6 py-3 border-b bg-background">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <h1 className="text-xl font-bold text-foreground" data-testid="text-profile-name">
                            {decodeURIComponent(profileName)}
                        </h1>
                        <span className="text-sm text-muted-foreground">• Policy Profile Details</span>
                    </div>
                    <Button
                        onClick={() => navigate(
                            `/policyProfiles/categories/${profileId}/${os}/${osType}`,
                            { state: { profileName } }
                        )}
                        data-testid="button-view-categories"
                    >
                        <FolderOpen className="w-4 h-4 mr-2" />
                        Categories
                    </Button>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="h-full flex flex-col bg-white dark:bg-background">
                <div className="flex border-b bg-white dark:bg-background">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => handleTabChange(tab.id)}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                                }`}
                            data-testid={`tab-${tab.id}`}
                        >
                            <div className="flex items-center gap-2">
                                <tab.icon className="w-4 h-4" />
                                {tab.name}
                            </div>
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="flex-1 bg-white dark:bg-background overflow-hidden">
                    {activeTab === "app-control" && (
                        <ApplicationControlTab
                            selectedUser="system"
                            onUserSelect={() => { }}
                            users={[]}
                            device={null as any}
                            profileId={profileId}
                            os={os}
                            fromDetails={true}
                        />
                    )}

                    {activeTab === "data-access" && (
                        <DataAccessTab
                            selectedUser="system"
                            onUserSelect={() => { }}
                            users={[]}
                            device={null as any}
                            profileId={profileId}
                            os={os}
                        />
                    )}

                    {activeTab === "privilege-mgmt" && (
                        <PrivilegeManagementTab
                            selectedUser="system"
                            onUserSelect={() => { }}
                            users={[]}
                            device={null as any}
                            profileId={profileId}
                            os={os}
                        />
                    )}

                    {activeTab === "url-control" && (
                        <URLControlTab
                            selectedUser="system"
                            onUserSelect={() => { }}
                            users={[]}
                            device={null as any}
                            profileId={profileId}
                            os={os}
                            fromDetails={true}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}